import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  CheckCircle2,
  Clock,
  Loader2,
  LogIn,
  LogOut,
  MapPin,
  MapPinOff,
  Signal,
  type LucideIcon,
} from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import { Button, cn } from "@eduviaos/ui-kit";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import { EASE_OUT } from "../../lib/motion";
import {
  clockTime,
  useCheckIn,
  type CheckInBody,
  type TeamAttendanceRecord,
  type TeamProfile,
} from "../../lib/team/api";
import { readLocation, type LocationResult } from "../../lib/team/location";

/**
 * Phase SC-22d — today's check-in / check-out for a team member.
 *
 * Location states the card can be in:
 *   locating        — waiting for the device
 *   inside          — counted at the centre (auto-approved in geo mode)
 *   outside         — recorded, waiting for the owner's approval
 *   accuracy_low    — the reading is too blurry; the BE refused it
 *   no_location     — permission denied / unavailable; can still check in
 *                     without a location, which always waits for approval
 */

type Phase =
  | { kind: "idle" }
  | { kind: "locating" }
  | { kind: "sending" }
  | { kind: "accuracy_low"; accuracy: number; limit: number | null }
  | { kind: "no_location"; reason: Exclude<LocationResult, { ok: true }>["reason"] }
  | { kind: "error"; message: string };

const NO_LOCATION_COPY: Record<Exclude<LocationResult, { ok: true }>["reason"], string> = {
  denied:
    "Location is blocked for this site. Allow it in your browser settings, or check in without it.",
  unavailable:
    "Your phone couldn't find its location. Step outside or near a window and try again.",
  timeout: "Finding your location took too long. Try again.",
  unsupported: "This browser can't share a location.",
};

function StatusLine({
  icon: Icon,
  tone,
  children,
}: {
  icon: LucideIcon;
  tone: "good" | "wait" | "warn" | "muted";
  children: React.ReactNode;
}) {
  return (
    <p
      className={cn(
        "flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm",
        tone === "good" && "bg-primary/10 text-primary",
        tone === "wait" &&
          "bg-amber-100/70 text-amber-900 dark:bg-amber-900/25 dark:text-amber-200",
        tone === "warn" && "bg-destructive/10 text-destructive",
        tone === "muted" && "bg-muted text-muted-foreground",
      )}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

function recordLine(rec: TeamAttendanceRecord, profile: TeamProfile): React.ReactNode {
  const inAt = clockTime(rec.check_in_at);
  const outAt = clockTime(rec.check_out_at);
  if (rec.approval_status === "rejected") {
    return (
      <StatusLine icon={MapPinOff} tone="warn">
        Today&apos;s check-in was not approved. Speak to your centre.
      </StatusLine>
    );
  }
  if (rec.approval_status === "pending") {
    return (
      <StatusLine icon={Clock} tone="wait">
        {rec.inside_geofence === false && rec.distance_m !== null
          ? `Checked in ${Math.round(rec.distance_m)} m from ${profile.location_label ?? "the centre"}. `
          : inAt
            ? `Checked in at ${inAt}. `
            : ""}
        Waiting for approval.
      </StatusLine>
    );
  }
  if (rec.check_in_at) {
    return (
      <StatusLine icon={CheckCircle2} tone="good">
        {outAt ? `In at ${inAt}, out at ${outAt}.` : `Checked in at ${inAt}.`}
        {rec.inside_geofence ? ` At ${profile.location_label ?? "the centre"}.` : ""}
      </StatusLine>
    );
  }
  return (
    <StatusLine icon={CheckCircle2} tone="good">
      Your centre marked today as{" "}
      {rec.status === "half_day" ? "a half day" : rec.status.replace("_", " ")}.
    </StatusLine>
  );
}

export function TeamToday({ profile, firstName }: { profile: TeamProfile; firstName: string }) {
  const reduce = useReducedMotion();
  const record = profile.today_record;
  const checkedIn = Boolean(record?.check_in_at);
  const checkedOut = Boolean(record?.check_out_at);
  const markedByCentre = Boolean(record && !record.check_in_at);
  const action: "check-in" | "check-out" = checkedIn ? "check-out" : "check-in";
  const mutation = useCheckIn(action);
  const [phase, setPhase] = React.useState<Phase>({ kind: "idle" });
  const [notice, setNotice] = React.useState<string | null>(null);

  const send = async (body: CheckInBody) => {
    setPhase({ kind: "sending" });
    try {
      const res = await mutation.mutateAsync(body);
      setNotice(res.message);
      setPhase({ kind: "idle" });
    } catch (err) {
      if (err instanceof ApiError) {
        const code = String(err.details?.code ?? err.code);
        if (code === "team_pay.accuracy_too_low") {
          const accuracy = Number(err.details?.accuracy_m ?? body.accuracy_m ?? 0);
          const limit = err.details?.max_accuracy_m;
          setPhase({
            kind: "accuracy_low",
            accuracy: Math.round(accuracy),
            limit: typeof limit === "number" ? limit : profile.max_accuracy_m,
          });
          return;
        }
        setPhase({ kind: "error", message: err.message });
        return;
      }
      setPhase({ kind: "error", message: "Couldn't reach your centre. Check your connection." });
    }
  };

  const start = async () => {
    setNotice(null);
    if (!profile.geofence_configured) {
      await send({});
      return;
    }
    setPhase({ kind: "locating" });
    const loc = await readLocation();
    if (!loc.ok) {
      setPhase({ kind: "no_location", reason: loc.reason });
      return;
    }
    const limit = profile.max_accuracy_m;
    if (limit !== null && loc.reading.accuracy > limit) {
      setPhase({ kind: "accuracy_low", accuracy: Math.round(loc.reading.accuracy), limit });
      return;
    }
    await send({ lat: loc.reading.lat, lng: loc.reading.lng, accuracy_m: loc.reading.accuracy });
  };

  const busy = phase.kind === "locating" || phase.kind === "sending";
  const canAct = profile.can_self_check_in && !checkedOut && !markedByCentre;
  const ActionIcon = checkedIn ? LogOut : LogIn;

  return (
    <div className="space-y-5">
      <Reveal>
        <SectionHeader title={`Hi ${firstName}`} level={1} />
        <SectionCard className="space-y-4" data-testid="team-today-card">
          <div className="flex items-center gap-3">
            <span className="bg-primary/12 text-primary grid size-12 shrink-0 place-items-center rounded-2xl">
              <MapPin aria-hidden="true" className="size-6" />
            </span>
            <div className="min-w-0">
              <p className="font-display text-lg font-semibold">
                {checkedOut
                  ? "Done for today"
                  : checkedIn
                    ? "You're checked in"
                    : markedByCentre
                      ? "Today is marked"
                      : "Not checked in yet"}
              </p>
              <p className="text-muted-foreground text-sm">
                {profile.role_label ?? "Team member"}
                {profile.geofence_configured && profile.location_label
                  ? ` · ${profile.location_label}`
                  : ""}
              </p>
            </div>
          </div>

          {record ? recordLine(record, profile) : null}

          <AnimatePresence initial={false} mode="wait">
            {notice && phase.kind === "idle" ? (
              <motion.div
                key="notice"
                initial={reduce ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0 }}
                transition={{ duration: 0.2, ease: EASE_OUT }}
                aria-live="polite"
              >
                <StatusLine icon={CheckCircle2} tone="muted">
                  {notice}
                </StatusLine>
              </motion.div>
            ) : null}
            {phase.kind === "accuracy_low" ? (
              <motion.div
                key="accuracy"
                initial={reduce ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: EASE_OUT }}
                role="alert"
              >
                <StatusLine icon={Signal} tone="warn">
                  Your location is only accurate to about {phase.accuracy} m
                  {phase.limit ? ` (needs ${phase.limit} m or better)` : ""}. Turn on precise
                  location or move near a window, then try again.
                </StatusLine>
              </motion.div>
            ) : null}
            {phase.kind === "no_location" ? (
              <motion.div
                key="noloc"
                initial={reduce ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: EASE_OUT }}
                role="alert"
                className="space-y-2"
              >
                <StatusLine icon={MapPinOff} tone="warn">
                  {NO_LOCATION_COPY[phase.reason]}
                </StatusLine>
              </motion.div>
            ) : null}
            {phase.kind === "error" ? (
              <motion.div
                key="error"
                initial={reduce ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: EASE_OUT }}
                role="alert"
              >
                <StatusLine icon={MapPinOff} tone="warn">
                  {phase.message}
                </StatusLine>
              </motion.div>
            ) : null}
          </AnimatePresence>

          {canAct ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                size="lg"
                className="h-12 flex-1 rounded-2xl text-base active:scale-[0.97]"
                disabled={busy}
                onClick={start}
                data-testid="team-check-action"
              >
                {busy ? (
                  <Loader2 aria-hidden="true" className="size-5 animate-spin" />
                ) : (
                  <ActionIcon aria-hidden="true" className="size-5" />
                )}
                {phase.kind === "locating"
                  ? "Finding your location…"
                  : phase.kind === "sending"
                    ? "Saving…"
                    : phase.kind === "accuracy_low" || phase.kind === "no_location"
                      ? "Try again"
                      : checkedIn
                        ? "Check out"
                        : "Check in"}
              </Button>
              {phase.kind === "no_location" ? (
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 rounded-2xl active:scale-[0.97]"
                  disabled={busy}
                  onClick={() =>
                    send({
                      note: checkedIn
                        ? "Checked out without location"
                        : "Checked in without location",
                    })
                  }
                  data-testid="team-check-without-location"
                >
                  {checkedIn ? "Check out without location" : "Check in without location"}
                </Button>
              ) : null}
            </div>
          ) : !profile.can_self_check_in && !record ? (
            <StatusLine icon={Clock} tone="muted">
              Your centre marks attendance for you.
            </StatusLine>
          ) : null}

          {canAct && profile.geofence_configured && !checkedIn ? (
            <p className="text-muted-foreground text-xs">
              We use your location once, only when you tap. Inside {profile.geofence_radius_m ?? 0}{" "}
              m of {profile.location_label ?? "the centre"} counts as at the centre.
            </p>
          ) : null}
        </SectionCard>
      </Reveal>
    </div>
  );
}
